<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<main-content
			v-if="!page.introblock"
			:title="page.title || page.header"
			:content="page.content"
			:intro-image="{
				image: page.introductionImage,
				imageWebp: page.introductionImageWebp,
			}"
		/>

		<intro-block v-if="page.introblock" :item="page.introblock" />

		<section class="main-content rooms-section">
			<div class="row row-header align-center">
				<h2 class="subtitle">{{ defaults[locale].rooms.subtitle }}</h2>
				<h2>{{ defaults[locale].rooms.sectionRoomsTitle }}</h2>
				<div v-parse-links v-html="defaults[locale].rooms.sectionRoomsContent" />
			</div>
			<rooms :items="roomsData">
				<template #extraContent="scope">
					<div class="room-icons">
						<div v-for="facility in scope.facilities" :key="facility.id" class="icon-holder">
							<picture>
								<source :srcset="facility.iconWebp" type="image/webp" />
								<source :srcset="facility.icon" />
								<img
									:src="facility.icon"
									:alt="facility.iconAlt"
									class="facility-icon"
									loading="lazy"
								/>
							</picture>
							<span>{{ facility.header }}</span>
						</div>
					</div>
					<div class="button-wrapper">
						<nuxt-link :to="scope.filename" class="button">
							{{ $t('viewThisRoom') }}
						</nuxt-link>
						<book-button class="button button-cta" :roomid="scope.bookingid">
							{{ $t('bookThisRoom') }}
						</book-button>
					</div>
				</template>
			</rooms>
		</section>

		<usp-block
			v-if="page.uSPBlock"
			:header="page.uSPBlock.header"
			:items="page.uSPBlock.items"
			:button="{
				text: page.uSPBlock.button,
				link: page.uSPBlock.buttonLink,
			}"
			color="white"
		/>

		<compare-block
			:header="defaults[locale].rooms.compareHeader"
			:room-size="defaults[locale].rooms.roomSize"
			:room-size-icon="{
				image: defaults[locale].rooms.roomSizeIcon,
				imageWebp: defaults[locale].rooms.roomSizeIconWebp,
			}"
			:items="roomsData"
			:facilities-items="roomsFacilitiesData"
		/>

		<contentblocks :items="page.contentblocks" />
		<frequently-asked-questions v-if="page.faqs && page.faqs.length" :items="page.faqs" />
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: roomsData } = await useWebsiteFetch('rooms', {
	query: { language: locale.value },
	key: `${locale.value}/rooms`,
});

const { data: roomsFacilitiesData } = await useWebsiteFetch('roomsFacilities', {
	query: { language: locale.value },
	key: `${locale.value}/roomsFacilities`,
});
</script>

<style lang="scss" scoped>
:deep(h1) {
	color: var(--header-color-senary);
}

.row-header {
	max-width: 1000px;
}

.room-icons {
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: flex-start;
	margin: 0 0 40px;
}

.button-wrapper {
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: flex-start;

	a {
		margin: 8px 12px 8px 0;
		min-width: 210px;
	}
}

.icon-holder {
	color: var(--button-nofill-color);
	width: 49%;
	margin: 10px 1% 10px 0;
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;

	img {
		margin: 0 15px 0 0;
		max-width: 25px;
	}
}

.icon-holder i,
.icon-holder svg {
	margin: 0 5px 0 0;
}

.room-item .room-icons {
	max-width: 600px;
}

.room-info {
	padding: 0 0 50px;

	.row {
		padding: 50px 30px;
	}
}

@media (max-width: 680px) {
	.icon-holder {
		width: 99%;
	}
}
</style>
